import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ScoreRing } from '@/components/ScoreRing';
import { ChevronLeft, Brain } from 'lucide-react';
import { format } from 'date-fns';

export default function Mindset() {
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [mood, setMood] = useState(7);
  const [note, setNote] = useState('');
  const [today_log, setTodayLog] = useState(null);
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);
  const [score, setScore] = useState(null);

  const load = () => {
    api.get('/mindset-logs', { params: { date: today } }).then(r => { if (r.data) { setTodayLog(r.data); setMood(r.data.mood); setNote(r.data.note || ''); } }).catch(() => {});
    api.get('/mindset-logs').then(r => setRecent(r.data.slice(0, 7)));
    api.get('/regen-score').then(r => setScore(r.data.mindset));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.post('/mindset-logs', { date: today, mood, note }); toast.success('Mindset logged'); load(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const moodLabel = mood >= 9 ? 'Amazing' : mood >= 7 ? 'Good' : mood >= 5 ? 'Okay' : mood >= 3 ? 'Low' : 'Struggling';

  return (
    <div className="space-y-5" data-testid="mindset-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">Mindset</h1>
      </div>

      <div className="regen-card-elevated p-5 flex items-center gap-5 blueprint-grid">
        <ScoreRing value={score ?? 0} color="#FFB800" size={110} stroke={10} label="Mindset" />
        <div className="flex-1">
          <div className="text-sm text-foreground/90">Today's mindset score reflects how you're feeling today and resets at midnight. Consistency here keeps your YourReGen Score strong.</div>
        </div>
      </div>

      <div className="regen-card-elevated p-5 space-y-4">
        <div className="flex items-center gap-2"><Brain className="w-5 h-5 text-[#FFB800]" /><div className="font-display font-bold">How do you feel today?</div></div>
        <div className="grid grid-cols-10 gap-1.5">
          {[1,2,3,4,5,6,7,8,9,10].map(n => (
            <button key={n} onClick={() => setMood(n)} data-testid={`mood-${n}`}
              className={`aspect-square rounded-full text-xs font-semibold transition ${mood === n ? 'bg-[#FFB800] text-[#081726] scale-110' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{n}</button>
          ))}
        </div>
        <div className="text-center text-sm text-[#FFB800] font-semibold">{mood}/10 · {moodLabel}</div>
        <textarea value={note} onChange={e => setNote(e.target.value)} data-testid="mindset-note" placeholder="Optional note — what's on your mind?"
          className="w-full bg-[var(--c-film)] border border-[var(--c-hair)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#FFB800] resize-none" rows={3} />
        <button onClick={save} disabled={saving} data-testid="save-mindset-btn" className="w-full rounded-xl py-3 bg-[#FFB800] text-[#081726] font-bold tracking-wide active:scale-[0.99] transition disabled:opacity-60">
          {saving ? 'SAVING…' : today_log ? 'UPDATE CHECK-IN' : 'SAVE CHECK-IN'}
        </button>
      </div>

      {recent.length > 0 && (
        <div className="regen-card-elevated p-5">
          <div className="label-mini mb-3">Recent Check-ins</div>
          <div className="space-y-2">
            {recent.map(r => (
              <div key={r.date} className="flex items-center gap-3">
                <div className="text-xs text-muted-foreground w-16">{r.date.slice(5)}</div>
                <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden"><div className="h-full rounded-full bg-[#FFB800]" style={{ width: `${r.mood * 10}%` }} /></div>
                <div className="font-mono text-sm w-8 text-right">{r.mood}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
